/**
 * @file AI component: the state of one finite state machine (GDD §10).
 *
 * The component stores which state the agent is in and the thresholds that make
 * it change; the transitions themselves live in
 * {@link ../systems/EnemyAISystem.js}. New behaviours (patrol, flank, cover)
 * become new states in that table plus new fields here — the existing states do
 * not have to change.
 *
 * The `enemy_suicide_bomber` archetype (issue #5) is the clearest example: it
 * adds five states (three idle variants, a charge and an exploding state) and a
 * block of tuning fields, all of it pure data. The bomber's *behaviour* lives
 * entirely in the system; the *numbers* live here and in its `ENEMIES` row.
 */

import { Component } from '../ecs/Component.js';
import { toRadians } from '../utils/MathUtils.js';
import { number, nonNegative, positive, oneOf, bool, warnParam } from '../utils/Validate.js';

/**
 * @readonly
 * @enum {string}
 */
export const AIState = Object.freeze({
  IDLE: 'idle',
  SEEK: 'seek',
  ATTACK: 'attack',
  /** Stand-off state: keeps `preferredRange` and fires a weapon. */
  RANGED: 'ranged',

  // --- Issue #5: suicide-bomber states ------------------------------------
  /** Stays put at its spawn, watching its detection cone. */
  IDLE_STATIC: 'idle_static',
  /** Wanders randomly inside a radius around its spawn. */
  IDLE_ROAMING: 'idle_roaming',
  /** Patrols progressively, picking destinations relative to itself. */
  IDLE_PATROL: 'idle_patrol',
  /** Runs at the player and detonates on contact or proximity. */
  AGGRO_CHARGE: 'aggro_charge',
  /** Falling back from the player after taking a beating (issue: AI overhaul). */
  RETREAT: 'retreat',
  /** Lost sight of the player: sweeping the last known position. */
  SEARCH: 'search',
  /** Gave up the hunt: walking back to its post, patching itself up. */
  RETURN_HOME: 'return_home',
  /** Committed to blowing up: locked, braking, counting down the fuse. */
  EXPLODING: 'exploding',

  /** Knocked senseless by a thrown weapon: cannot move, shoot or scavenge. */
  STUNNED: 'stunned',
  /** Unarmed and walking to a weapon lying on the floor. */
  SEEK_WEAPON: 'seek_weapon',
  DEAD: 'dead'
});

/** Idle states a bomber may start in, or fall back to when it loses a target. */
export const IDLE_STATES = Object.freeze([
  AIState.IDLE_STATIC,
  AIState.IDLE_ROAMING,
  AIState.IDLE_PATROL
]);

/** States a `starter_state` value is allowed to name. */
const STARTER_STATES = Object.freeze([...IDLE_STATES, AIState.AGGRO_CHARGE]);

export class AIBrain extends Component {
  /**
   * @param {object} [options] Behaviour thresholds. All fields are optional and
   *   fall back to a safe default; invalid values warn in development builds.
   * @param {string} [options.name] Owner label used in validation warnings.
   * @param {number} [options.seekRange] Distance at which the agent starts chasing.
   * @param {number} [options.attackRange] Distance at which it attacks.
   * @param {number} [options.attackRate] Attacks per second.
   * @param {number} [options.damage] Damage per attack.
   * @param {number} [options.separationRadius] Distance kept from other agents.
   * @param {number} [options.separationStrength] Push strength applied within that radius.
   * @param {'melee' | 'ranged'} [options.behavior] Which branch the FSM takes from Seek.
   * @param {number} [options.preferredRange] Stand-off distance for ranged agents.
   * @param {string} [options.starterState] Initial FSM state (bomber archetypes).
   * @param {DetectionConfig} [options.detection] Vision-cone parameters.
   * @param {AggroConfig} [options.aggro] Charge/pursuit parameters.
   * @param {IdleMoveConfig} [options.idle] Idle-movement parameters.
   * @param {number} [options.weaponSearchRange] Radius searched for a floor weapon.
   * @param {number} [options.radius] Body radius, fed to nav-grid clearance/pathing.
   */
  constructor(options = {}) {
    super();
    const who = options.name ?? 'AIBrain';

    /** @type {number} Seconds spent in the current state. */
    this.timeInState = 0;
    /** @type {import('../ecs/Entity.js').Entity | null} Current target. */
    this.target = null;

    /** @type {number} */
    this.seekRange = nonNegative(who, 'seekRange', options.seekRange, 40);
    /** @type {number} */
    this.attackRange = nonNegative(who, 'attackRange', options.attackRange, 1.5);
    /** @type {number} */
    this.attackRate = nonNegative(who, 'attackRate', options.attackRate, 1);
    /** @type {number} */
    this.damage = nonNegative(who, 'damage', options.damage, 5);
    /** @type {number} */
    this.separationRadius = nonNegative(who, 'separationRadius', options.separationRadius, 1.5);
    /** @type {number} */
    this.separationStrength = nonNegative(who, 'separationStrength', options.separationStrength, 6);
    /** @type {'melee' | 'ranged'} */
    this.behavior = oneOf(who, 'behavior', options.behavior, ['melee', 'ranged'], 'melee');
    /** @type {number} Distance a ranged agent tries to hold. */
    this.preferredRange = nonNegative(who, 'preferredRange', options.preferredRange, 0);
    /**
     * Body radius, passed to `NavGrid.isClear`/`findPath` so a wide brute never
     * accepts a route through a gap a slim runner would fit.
     * @type {number}
     */
    this.radius = positive(who, 'radius', options.radius, 0.5);
    /** @type {number} Seconds left before the next attack. */
    this.attackCooldown = 0;
    /** @type {number} Seconds left in the current melee swing animation. */
    this.meleeSwingRemaining = 0;
    /** @type {number} Total duration of one melee swing animation. */
    this.meleeSwingDuration = 0.26;
    /** @type {number} Strafe direction, flipped periodically so agents circle. */
    this.strafeSign = Math.random() < 0.5 ? -1 : 1;

    // --- Issue #5: suicide-bomber configuration ---------------------------

    /**
     * Initial state. An invalid value falls back to `IDLE_STATIC` and warns.
     * @type {string}
     */
    this.starterState = oneOf(who, 'starter_state', options.starterState, STARTER_STATES, AIState.IDLE_STATIC);
    /**
     * Current FSM state. Seeded from {@link starterState} so a bomber begins in
     * its configured idle behaviour rather than the generic `IDLE`.
     * @type {string}
     */
    this.state = options.starterState !== undefined ? this.starterState : AIState.IDLE;
    /** @type {string} The state a homebound agent settles back into. */
    this.homeState = this.state;

    const detection = options.detection ?? {};
    /** @type {number} Vision-cone reach in world units. */
    this.detectionConeRange = nonNegative(who, 'enemy_detection_cone_range', detection.range, 0);
    /**
     * Half-angle of the vision cone, in radians. The spec's
     * `enemy_detection_cone_angle` is the *full* apex angle in degrees; anything
     * outside (0, 360] is rejected.
     * @type {number}
     */
    this.detectionConeHalfAngle =
      toRadians(number(who, 'enemy_detection_cone_angle', detection.angle, 120, (a) => a > 0 && a <= 360, 'must be in (0, 360] degrees') / 2);
    /** @type {number} Seconds between vision checks (throttles the LOS raycast). */
    this.detectionCheckSeconds = positive(who, 'enemy_detection_check_seconds', detection.checkSeconds, 0.2);

    const aggro = options.aggro ?? {};
    /** @type {number} Flat charge speed when acceleration is disabled. */
    this.aggroChargeSpeed = nonNegative(who, 'aggro_charge_speed', aggro.chargeSpeed, 7);
    /** @type {number} How fast the body turns toward its heading (approach rate). */
    this.aggroRotationSpeed = nonNegative(who, 'aggro_rotation_speed', aggro.rotationSpeed, 10);
    /** @type {number} Seconds between forced re-paths toward the player. */
    this.aggroRepathSeconds = positive(who, 'aggro_repath_seconds', aggro.repathSeconds, 0.35);
    /** @type {number} Player displacement that forces an early re-path. */
    this.aggroRepathMovementThreshold = nonNegative(who, 'aggro_repath_target_movement_threshold', aggro.repathMovementThreshold, 2);
    /** @type {boolean} If false, the bomber never gives up a valid target. */
    this.aggroCanLoseTarget = bool(who, 'aggro_can_lose_target', aggro.canLoseTarget, false);
    /** @type {number} Seconds it keeps chasing after losing sight, when allowed. */
    this.aggroTargetMemorySeconds = nonNegative(who, 'aggro_target_memory_seconds', aggro.targetMemorySeconds, 3);
    /** @type {string} Idle state entered when memory expires. */
    this.aggroLostTargetReturnState = oneOf(who, 'aggro_lost_target_return_state', aggro.lostTargetReturnState, IDLE_STATES, AIState.IDLE_STATIC);
    /** @type {string} Idle state entered when the target becomes invalid. */
    this.invalidTargetReturnState = oneOf(who, 'invalid_target_return_state', aggro.invalidTargetReturnState, IDLE_STATES, AIState.IDLE_STATIC);

    // Optional charge acceleration ramp.
    /** @type {boolean} */
    this.aggroAccelerationEnabled = bool(who, 'aggro_charge_acceleration_enabled', aggro.accelerationEnabled, false);
    /** @type {number} */
    this.aggroInitialSpeed = nonNegative(who, 'aggro_charge_initial_speed', aggro.initialSpeed, this.aggroChargeSpeed);
    /** @type {number} */
    this.aggroMaxSpeed = nonNegative(who, 'aggro_charge_max_speed', aggro.maxSpeed, Math.max(this.aggroChargeSpeed, this.aggroInitialSpeed));
    if (this.aggroMaxSpeed < this.aggroInitialSpeed) {
      // Spec: max may not be below initial. Clamp up and warn once.
      warnParam(who, 'aggro_charge_max_speed', aggro.maxSpeed, this.aggroInitialSpeed, 'must be >= aggro_charge_initial_speed');
      this.aggroMaxSpeed = this.aggroInitialSpeed;
    }
    /** @type {number} */
    this.aggroAccelerationPerSecond = nonNegative(who, 'aggro_charge_acceleration_per_second', aggro.accelerationPerSecond, 6);

    // Stuck detection (steering can wedge against geometry with no navmesh).
    /** @type {number} */
    this.navArrivalTolerance = positive(who, 'navigation_arrival_tolerance', aggro.arrivalTolerance, 0.6);
    /** @type {number} */
    this.navStuckSeconds = positive(who, 'navigation_stuck_detection_seconds', aggro.stuckDetectionSeconds, 0.6);
    /** @type {number} */
    this.navStuckDistance = nonNegative(who, 'navigation_stuck_distance_threshold', aggro.stuckDistanceThreshold, 0.35);

    const idle = options.idle ?? {};
    /** @type {number} Movement speed in the roaming/patrol idle states. */
    this.idleWalkSpeed = nonNegative(who, 'idle_walk_speed', idle.walkSpeed, 1.8);
    /** @type {number} */
    this.idleRoamingSelectSeconds = positive(who, 'idle_roaming_select_path_seconds', idle.roamingSelectSeconds, 3);
    /** @type {number} */
    this.idleRoamingRange = nonNegative(who, 'idle_roaming_path_range', idle.roamingRange, 6);
    /** @type {number} */
    this.idlePatrolSelectSeconds = positive(who, 'idle_patrol_select_path_seconds', idle.patrolSelectSeconds, 3);
    /** @type {number} */
    this.idlePatrolRange = nonNegative(who, 'idle_patrol_path_range', idle.patrolRange, 8);
    /**
     * Global leash from the spawn point for patrol. `<= 0` means no leash.
     * @type {number}
     */
    this.idlePatrolMaxDistanceFromSpawn = number(who, 'idle_patrol_max_distance_from_spawn', idle.patrolMaxDistanceFromSpawn, 0);

    // --- Per-instance runtime scratch (never shared between instances) -----
    /** @type {number} Spawn position, the anchor for idle behaviours. */
    this.spawnX = 0;
    /** @type {number} */
    this.spawnZ = 0;
    /** @type {number} Facing captured at spawn, held by Idle_Static. */
    this.spawnRotationY = 0;
    /** @type {number} Counts down to the next detection check. */
    this.detectionTimer = 0;
    /** @type {number} Counts down to the next idle destination pick. */
    this.idleSelectTimer = 0;
    /** @type {boolean} Whether a navigation destination is active. */
    this.hasNavTarget = false;
    /** @type {number} Current navigation destination (the next waypoint to steer at). */
    this.navTargetX = 0;
    /** @type {number} */
    this.navTargetZ = 0;
    /**
     * Active A* route around geometry while chasing, or null for direct
     * steering. Mirrors {@link ./RangedAI.js RangedAI}'s `path`/`pathIndex`.
     * @type {Array<{ x: number, z: number }> | null}
     */
    this.path = null;
    /** @type {number} Index of the waypoint currently being walked. */
    this.pathIndex = 0;
    /** @type {number} Counts down to the next forced re-path. */
    this.repathTimer = 0;
    /** @type {number} Player position the current path was planned against. */
    this.pathPlannedForX = 0;
    /** @type {number} */
    this.pathPlannedForZ = 0;
    /**
     * Live charge speed when the acceleration ramp is on.
     * Seeded to the correct start speed so a `starter_state` of `AGGRO_CHARGE`
     * does not begin at 0 (enter hooks only run on transitions, not spawn).
     * @type {number}
     */
    this.currentChargeSpeed = this.aggroAccelerationEnabled ? this.aggroInitialSpeed : this.aggroChargeSpeed;
    /** @type {number} Seconds of near-zero progress; drives stuck recovery. */
    this.stuckTimer = 0;
    /** @type {number} Position sampled at the start of the stuck window. */
    this.stuckSampleX = 0;
    /** @type {number} */
    this.stuckSampleZ = 0;
    /** @type {boolean} Whether the agent is currently wedged. */
    this.isStuck = false;
    /** @type {boolean} Set the frame the agent takes damage; forces aggro. */
    this.wasDamaged = false;
    /** @type {boolean} Whether a live target is currently visible. */
    this.hasLineOfSight = false;
    /** @type {number} Seconds since the target was last seen. */
    this.timeSinceSeen = 0;
    /** @type {number} Last known player position (for memory chasing). */
    this.lastKnownX = 0;
    /** @type {number} */
    this.lastKnownZ = 0;
    /** @type {number} Seconds left of the thrown-weapon stun; > 0 forces `STUNNED`. */
    this.stunRemaining = 0;
    /** @type {number} Seconds until the next ring of stun stars is emitted. */
    this.stunFxCooldown = 0;
    /** @type {number} Angle of the star ring, advanced so it reads as spinning. */
    this.stunSpin = 0;
    /** @type {number} Radius searched for a weapon on the floor. */
    this.weaponSearchRange = options.weaponSearchRange ?? 20;

    // --- AI-overhaul knobs (all optional; defaults keep old behaviour) -----
    /** @type {number} Radius of the aggro shout that wakes idle allies; 0 disables. */
    this.alertRadius = Math.max(0, options.alertRadius ?? 0);
    /**
     * How far this agent hears the player's gunfire. Hearing gives a position
     * to investigate, never a live track — sound is a clue, not a wallhack.
     * @type {number}
     */
    this.hearingRadius = Math.max(0, options.hearingRadius ?? 14);
    /** @type {number} Seconds spent sweeping a last-known position before giving up. */
    this.searchSeconds = options.searchSeconds ?? 5;
    /** @type {number} Radius of the sweep around the last known position. */
    this.searchRadius = options.searchRadius ?? 4;
    /** @type {number} Health regained per second (fraction of max) while walking home. */
    this.homeHealRate = options.homeHealRate ?? 0.25;
    /** @type {boolean} Pack leader: shares intel, holds morale, panics allies on death. */
    this.leader = Boolean(options.leader);
    /** @type {number} Counts down the active search sweep. */
    this.searchTimer = 0;
    /** @type {number} Perception throttle, so LOS rays are not cast every frame. */
    this.perceptionTimer = 0;
    /**
     * Health ratio at or below which the agent falls back once per life.
     * 0 disables: brutes and bombers never blink.
     * @type {number}
     */
    this.retreatHealthRatio = Math.max(0, options.retreatHealthRatio ?? 0);
    /** @type {number} Seconds a retreat lasts before re-engaging. */
    this.retreatSeconds = options.retreatSeconds ?? 2.2;
    /** @type {boolean} One retreat per life: latched on first use. */
    this.hasRetreated = false;
    /** @type {number} Counts down the active retreat. */
    this.retreatTimer = 0;

    // Snapshot of the archetype's combat profile. Equipping a scavenged weapon
    // overwrites the live fields; losing it restores these, so a stolen weapon
    // never permanently rewrites what the archetype is.
    /** @type {'melee' | 'ranged'} */
    this.baseBehavior = this.behavior;
    /** @type {number} */
    this.baseAttackRange = this.attackRange;
    /** @type {number} */
    this.basePreferredRange = this.preferredRange;
  }

  /**
   * Retunes the agent around a weapon it just picked up, so a melee grunt that
   * scavenges the player's shotgun actually stands off and shoots with it.
   * @param {import('./Weapon.js').Weapon} weapon Weapon now equipped.
   * @returns {void}
   */
  armWith(weapon) {
    this.behavior = 'ranged';
    this.attackRange = Math.min(weapon.range * 0.6, this.seekRange);
    this.preferredRange = this.attackRange * 0.7;
  }

  /**
   * Undoes {@link AIBrain#armWith}: back to whatever the archetype declared.
   * @returns {void}
   */
  disarm() {
    this.behavior = this.baseBehavior;
    this.attackRange = this.baseAttackRange;
    this.preferredRange = this.basePreferredRange;
  }

  /**
   * Switches state, resetting the per-state timer. Once `DEAD`, the agent can
   * never leave it (issue #5: "the instance does not change from Dead").
   * @param {string} next One of {@link AIState}.
   * @returns {void}
   */
  setState(next) {
    if (this.state === next) return;
    if (this.state === AIState.DEAD) return;
    this.state = next;
    this.timeInState = 0;
  }

  /** @returns {void} */
  reset() {
    this.state = AIState.IDLE;
    this.timeInState = 0;
    this.target = null;
    this.attackCooldown = 0;
    this.meleeSwingRemaining = 0;
    this.path = null;
    this.pathIndex = 0;
    this.hasNavTarget = false;
    this.stunRemaining = 0;
    this.stunFxCooldown = 0;
    this.stunSpin = 0;
    this.disarm();
  }
}

/**
 * @typedef {object} DetectionConfig
 * @property {number} [range] Cone reach in world units.
 * @property {number} [angle] Full apex angle in degrees, (0, 360].
 * @property {number} [checkSeconds] Seconds between vision checks.
 */

/**
 * @typedef {object} AggroConfig
 * @property {number} [chargeSpeed]
 * @property {number} [rotationSpeed]
 * @property {number} [repathSeconds]
 * @property {number} [repathMovementThreshold]
 * @property {boolean} [canLoseTarget]
 * @property {number} [targetMemorySeconds]
 * @property {string} [lostTargetReturnState]
 * @property {string} [invalidTargetReturnState]
 * @property {boolean} [accelerationEnabled]
 * @property {number} [initialSpeed]
 * @property {number} [maxSpeed]
 * @property {number} [accelerationPerSecond]
 * @property {number} [arrivalTolerance]
 * @property {number} [stuckDetectionSeconds]
 * @property {number} [stuckDistanceThreshold]
 */

/**
 * @typedef {object} IdleMoveConfig
 * @property {number} [walkSpeed]
 * @property {number} [roamingSelectSeconds]
 * @property {number} [roamingRange]
 * @property {number} [patrolSelectSeconds]
 * @property {number} [patrolRange]
 * @property {number} [patrolMaxDistanceFromSpawn]
 */
